/**
 * Pure mappers: backend extraction (`ExtractedContractDto`) → default wizard decisions,
 * and UI rule specs → the Planning `CreateTaskScheduleRuleRequest` shape.
 *
 * Kept free of store/React imports so the mapping stays unit-testable. Matching reuses
 * the validated netzplan fuzzy matcher (`match.ts`).
 */

import { ScheduleRuleType, Weekday, type FormattableRule } from '@/utils/scheduleRuleConstants';
import { matchCustomerName, matchObject, matchService } from '@/pages/Import/logic/match';
import type {
  ContactDecision,
  CustomerDecision,
  ExtractedContract,
  LiveData,
  MatchInfo,
  ObjectDecision,
  OfferDecision,
  PositionDecision,
  RecurrenceKindValue,
  RuleSpec,
  ScheduleRuleRequest,
} from './types';
import { RecurrenceKind } from './types';
import { billingFromTaskSchedule, type BillingRule } from '@/utils/billingKind';
import type { LiveCustomer, LiveObject, LiveService } from '@/pages/Import/logic/types';

/** Soft-trim with ellipsis — used to pre-fill values within the DB column limits. */
function truncate(value: string, maxLength: number): string {
  const trimmed = value.trim();
  return trimmed.length <= maxLength ? trimmed : `${trimmed.slice(0, maxLength - 1).trimEnd()}…`;
}

/** English lowercase weekday names (extraction contract) → `System.DayOfWeek` values. */
const WEEKDAY_NAME_TO_ENUM: Record<string, number> = {
  monday: Weekday.Monday,
  tuesday: Weekday.Tuesday,
  wednesday: Weekday.Wednesday,
  thursday: Weekday.Thursday,
  friday: Weekday.Friday,
  saturday: Weekday.Saturday,
  sunday: Weekday.Sunday,
};

let ruleIdCounter = 0;
export function newRuleId(): string {
  ruleIdCounter += 1;
  return `rule-${ruleIdCounter}`;
}

/** Full-year window — BiWeekly/Seasonal rules require a window on the BE. */
export function defaultWindow(): Pick<RuleSpec, 'windowFromMonth' | 'windowFromDay' | 'windowToMonth' | 'windowToDay'> {
  return { windowFromMonth: 1, windowFromDay: 1, windowToMonth: 12, windowToDay: 31 };
}

export function newRuleSpec(type: number): RuleSpec {
  return { id: newRuleId(), type, weekdays: [], ...defaultWindow() };
}

type ExtractedRecurrence = NonNullable<
  NonNullable<ExtractedContract['positions']>[number]['recurrence']
>;

/**
 * Extraction recurrence → default rule specs. `oneOff`/`unknown` yield no rule —
 * a task definition without rules is allowed (it simply never becomes due).
 */
export function mapRecurrenceToRuleSpecs(recurrence: ExtractedRecurrence | undefined): RuleSpec[] {
  const kind = recurrence?.kind ?? 'unknown';

  const seasonWindow = () => ({
    windowFromMonth: recurrence?.seasonFromMonth ?? 1,
    windowFromDay: recurrence?.seasonFromDay ?? 1,
    windowToMonth: recurrence?.seasonToMonth ?? 12,
    windowToDay: recurrence?.seasonToDay ?? 31,
  });

  switch (kind) {
    case 'weekly': {
      const weekdays = (recurrence?.weekdays ?? [])
        .map((name) => WEEKDAY_NAME_TO_ENUM[name.toLowerCase()])
        .filter((d): d is number => d !== undefined);
      return [{ id: newRuleId(), type: ScheduleRuleType.Weekly, weekdays, ...defaultWindow() }];
    }
    case 'biweekly':
      // BE requires a window for BiWeekly; without a season in the document it is all-year.
      return [{ id: newRuleId(), type: ScheduleRuleType.BiWeekly, weekdays: [], ...seasonWindow() }];
    case 'monthly':
      return [{ id: newRuleId(), type: ScheduleRuleType.Monthly, weekdays: [], ...defaultWindow() }];
    case 'seasonal':
      return [{ id: newRuleId(), type: ScheduleRuleType.Seasonal, weekdays: [], ...seasonWindow() }];
    default:
      return [];
  }
}

/**
 * UI rule spec → Planning create request. Only the fields the BE validator allows for
 * the type are sent (Weekly forbids a window; BiWeekly/Seasonal require one; Monthly
 * without weekday+ordinal means "once per month, any day").
 */
export function ruleSpecToRequest(spec: RuleSpec): ScheduleRuleRequest {
  // The generated enums are numeric literal unions; the UI spec deliberately works with
  // plain numbers (select values, chips), so narrow at the contract boundary.
  const type = spec.type as ScheduleRuleRequest['type'];
  switch (spec.type) {
    case ScheduleRuleType.Weekly:
      return {
        type,
        weekdays: spec.weekdays.length > 0 ? (spec.weekdays as ScheduleRuleRequest['weekdays']) : null,
      };
    case ScheduleRuleType.BiWeekly:
    case ScheduleRuleType.Seasonal:
      return {
        type,
        windowFromMonth: spec.windowFromMonth,
        windowFromDay: spec.windowFromDay,
        windowToMonth: spec.windowToMonth,
        windowToDay: spec.windowToDay,
      };
    default:
      return { type };
  }
}

/** UI rule spec → the minimal shape `formatScheduleRule` previews (mirrors what is sent). */
export function specToFormattable(spec: RuleSpec): FormattableRule {
  if (spec.type === ScheduleRuleType.Weekly) return { type: spec.type, weekdays: spec.weekdays };
  if (spec.type === ScheduleRuleType.BiWeekly || spec.type === ScheduleRuleType.Seasonal) {
    return {
      type: spec.type,
      windowFromMonth: spec.windowFromMonth,
      windowFromDay: spec.windowFromDay,
      windowToMonth: spec.windowToMonth,
      windowToDay: spec.windowToDay,
    };
  }
  return { type: spec.type };
}

/** Free-text `serviceCategoryHint` → `ServiceCategory` enum name. */
export function mapCategoryHint(hint: string | null | undefined): string {
  const h = (hint ?? '').toLowerCase();
  if (!h) return 'Other';
  if (/sanit|hygien|wc|toilet/.test(h)) return 'Sanitary';
  if (/stiege|treppe|stair|hausbetreuung/.test(h)) return 'Stairwell';
  if (/gr(ü|u)n|garten|rasen|au(ß|ss)en|hecke|green|winterdienst|schnee/.test(h)) return 'GreenSpace';
  if (/reinig|clean|putz/.test(h)) return 'Cleaning';
  return 'Other';
}

export interface ProposedBilling {
  kind: RecurrenceKindValue;
  rule: BillingRule;
}

/** Month (1–12) of an ISO date, else the current month — the start of a quarterly/yearly rhythm. */
export function startMonthOf(isoDate: string | null | undefined, today: Date = new Date()): number {
  const month = Number(isoDate?.slice(5, 7));
  return month >= 1 && month <= 12 ? month : today.getMonth() + 1;
}

/**
 * How the stated price is billed (FAFD-27): the price wording decides the kind — a price per
 * visit is billed by effort (each planned day), a monthly price in a season only in its months.
 */
export function billingFor(
  priceKind: string | null | undefined,
  scheduleRules: RuleSpec[],
  startMonth: number,
): ProposedBilling {
  switch (priceKind) {
    case 'monthlyFlat':
    case 'includedInFlatRate':
      // why: the request shape drops the full-year default window of weekly rules, so only a
      // real season limits the months.
      return billingFromTaskSchedule(
        scheduleRules.map(ruleSpecToRequest) as Parameters<typeof billingFromTaskSchedule>[0],
      );
    case 'quarterlyFlat':
      return { kind: RecurrenceKind.Quarterly, rule: { month: startMonth, inArrears: false } };
    case 'yearlyFlat':
      return { kind: RecurrenceKind.Yearly, rule: { month: startMonth, inArrears: false } };
    case 'oneOff':
      return { kind: RecurrenceKind.OneOff, rule: {} };
    case 'perService':
      return { kind: RecurrenceKind.ByEffort, rule: {} };
    default:
      return { kind: RecurrenceKind.Unknown, rule: {} };
  }
}

/** `pricePeriod` of a combined flat rate as a `priceKind`; `unknown` stays monthly as before. */
const FLAT_RATE_PRICE_KIND: Record<string, string> = {
  month: 'monthlyFlat',
  quarter: 'quarterlyFlat',
  year: 'yearlyFlat',
  once: 'oneOff',
};

/** Domain data (goes into the German offer document), not a UI string. */
const FLAT_RATE_TITLE: Partial<Record<RecurrenceKindValue, string>> = {
  [RecurrenceKind.Monthly]: 'Monatspauschale',
  [RecurrenceKind.Quarterly]: 'Quartalspauschale',
  [RecurrenceKind.Yearly]: 'Jahrespauschale',
};

const DEFAULT_DURATION_MINUTES = 60;
const DEFAULT_TAX_RATE = 20;

/** Offer contact person derived from the contact decision; empty when no contact is created. */
export function contactPersonNameOf(contact: ContactDecision): string {
  return contact.enabled ? `${contact.firstName.trim()} ${contact.lastName.trim()}`.trim() : '';
}

export interface DefaultDecisions {
  customer: CustomerDecision;
  customerMatch: MatchInfo<LiveCustomer>;
  contact: ContactDecision;
  object: ObjectDecision;
  objectMatch: MatchInfo<LiveObject>;
  positions: PositionDecision[];
  offer: OfferDecision;
}

/** Seed all wizard decisions from the extraction + live data. */
export function buildDefaultDecisions(extraction: ExtractedContract, live: LiveData): DefaultDecisions {
  // --- Customer ---------------------------------------------------------
  const customerName = extraction.customer?.name ?? '';
  const cm = matchCustomerName(customerName, live.customers);
  const customerLinks = cm.category === 'MATCH' && !!cm.suggestion;
  const customer: CustomerDecision = {
    mode: customerLinks ? 'link' : 'create',
    existingCustomerId: customerLinks ? cm.suggestion!.id : '',
    name: customerName,
    street: extraction.customer?.street ?? '',
    zip: extraction.customer?.zip ?? '',
    city: extraction.customer?.city ?? '',
    country: extraction.customer?.country ?? '',
    vatId: extraction.customer?.vatId ?? '',
  };

  // --- Contact ----------------------------------------------------------
  const c = extraction.contact;
  const contact: ContactDecision = {
    enabled: !!c && !!(c.firstName || c.lastName),
    firstName: c?.firstName ?? '',
    lastName: c?.lastName ?? '',
    role: c?.role ?? '',
    email: c?.email ?? '',
    phone: c?.phone ?? '',
  };

  // --- Object -----------------------------------------------------------
  const objName = extraction.object?.name ?? '';
  // A linked object must belong to the resolved customer (BE guard) — so only
  // suggest links when the customer itself resolves to an existing record.
  const objectsOfCustomer = customerLinks
    ? live.objects.filter((o) => o.customerId === cm.suggestion!.id)
    : [];
  const om = matchObject({ objName: objName || (extraction.object?.street ?? '') }, objectsOfCustomer);
  const objectLinks = om.category === 'MATCH' && !!om.suggestion;
  const object: ObjectDecision = {
    mode: objectLinks ? 'link' : 'create',
    existingObjectId: objectLinks ? om.suggestion!.id : '',
    name: objName,
    street: extraction.object?.street ?? '',
    zip: extraction.object?.zip ?? '',
    city: extraction.object?.city ?? '',
    country: extraction.object?.country ?? '',
  };

  // --- Positions --------------------------------------------------------
  const startMonth = startMonthOf(extraction.meta?.startDate);
  const positions: PositionDecision[] = (extraction.positions ?? []).map((p, index) => {
    const title = truncate(p.title ?? '', 200);
    const sm: MatchInfo<LiveService> = matchService(title, live.services);
    const serviceLinks = sm.category === 'MATCH' && !!sm.suggestion;
    const scheduleRules = mapRecurrenceToRuleSpecs(p.recurrence);
    const billing = billingFor(p.priceKind, scheduleRules, startMonth);
    return {
      key: `pos-${p.positionNumber ?? index}-${index}`,
      include: true,
      positionNumber: p.positionNumber ?? index + 1,
      title,
      service: {
        mode: serviceLinks ? 'link' : 'create',
        existingServiceId: serviceLinks ? sm.suggestion!.id : '',
        name: title,
        category: mapCategoryHint(p.serviceCategoryHint),
      },
      serviceMatch: sm,
      taskName: title,
      durationMinutes: DEFAULT_DURATION_MINUTES,
      scheduleRules,
      checklistItems: (p.activities ?? [])
        .filter((a) => !!a.title)
        .map((a) => ({
          // Soft-trim to the DB column limits (Title 300, IntervalHint 100) so an
          // over-long extraction pre-fills a valid, editable value instead of running
          // into the commit validation.
          title: truncate(a.title ?? '', 300),
          intervalHint: truncate(a.intervalHint ?? '', 100),
        })),
      offerTitle: title,
      intervalText: truncate(p.intervalText ?? p.recurrence?.rawText ?? '', 200),
      netAmount: p.netAmount ?? 0,
      recurrenceKind: billing.kind,
      billingRule: billing.rule,
      priceKind: p.priceKind ?? 'unknown',
    };
  });

  // --- Combined flat rates: default allocation --------------------------
  // The full flat amount goes to the first covered position, billed in the flat rate's own
  // period; the remaining covered positions carry 0 so the offer total matches the contract.
  for (const flat of extraction.combinedFlatRates ?? []) {
    const nums = flat.appliesToPositionNumbers ?? [];
    if (nums.length === 0) continue;
    const covered = positions.filter((p) => nums.includes(p.positionNumber));
    if (covered.length === 0) continue;
    const lo = Math.min(...nums);
    const hi = Math.max(...nums);
    // why: the covered positions' own schedules do not decide the flat rate's rhythm.
    const billing = billingFor(FLAT_RATE_PRICE_KIND[flat.pricePeriod ?? ''] ?? 'monthlyFlat', [], startMonth);
    covered.forEach((p, i) => {
      p.recurrenceKind = billing.kind;
      p.billingRule = { ...billing.rule };
      if (i === 0) {
        p.netAmount = flat.netAmount ?? 0;
        const label = FLAT_RATE_TITLE[billing.kind] ?? 'Pauschale';
        p.offerTitle = `${p.title} – ${label} Pos. ${lo === hi ? lo : `${lo}–${hi}`}`;
        if (flat.intervalText) p.intervalText = flat.intervalText;
      } else {
        p.netAmount = 0;
      }
    });
  }

  // --- Offer ------------------------------------------------------------
  const offer: OfferDecision = {
    offerDate: extraction.meta?.startDate ?? new Date().toISOString().slice(0, 10),
    taxRate: DEFAULT_TAX_RATE,
    contactPersonName: contactPersonNameOf(contact),
    introductionText: '',
    closingText: '',
    paymentTerms: extraction.meta?.paymentTerms ?? '',
  };

  return { customer, customerMatch: cm, contact, object, objectMatch: om, positions, offer };
}
